import { useAtom, atom, WritableAtom } from 'jotai';
import { isEqual } from 'lodash';

import { store } from './store';

// Base Class for Jotai State Controllers
export class StateController<T extends Record<string, any>> {
  protected store: typeof store;
  protected controllerName: string;
  protected initialState: T;
  protected state: WritableAtom<T, [T], void>;
  protected focusState: Record<string, WritableAtom<any, [any], void>>;

  constructor(initialState: T, controllerName: string) {
    this.store = store;
    this.controllerName = controllerName;
    this.initialState = initialState;
    this.state = atom(this.initialState);
    this.focusState = {};

    Object.keys(this.initialState).forEach(key => {
      this.getFocusItem(key, true);
    });
  }

  autoBind(instance: this): void {
    let proto = Object.getPrototypeOf(instance);
    while (proto !== null) {
      const methodNames = Object.getOwnPropertyNames(proto).filter(
        prop =>
          typeof instance[prop as keyof this] === 'function' && prop !== 'constructor'
      );
      for (const name of methodNames) {
        (instance as any)[name] = (instance as any)[name].bind(instance);
      }
      proto = Object.getPrototypeOf(proto);
    }
  }

  getFocusItem<K extends keyof T>(
    key: string,
    fromInitialState?: boolean
  ): WritableAtom<any, [any], void> {
    if (!this.focusState[key]) {
      // Create a simple atom for the key
      this.focusState[key] = atom(
        get => {
          const state = get(this.state);
          return key.split('.').reduce((obj: any, part) => obj?.[part], state);
        },
        (get, set, newValue) => {
          const currentState = get(this.state);
          const keys = key.split('.');
          const newState = { ...currentState };
          let current: any = newState;

          for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) {
              current[keys[i]] = {};
            }
            current = current[keys[i]];
          }

          current[keys[keys.length - 1]] = newValue;
          set(this.state, newState);
        }
      );
      if (!fromInitialState) {
        console.warn(
          `Key: ${key} does not exist in initial State of ${this.controllerName}. Add it to prevent issues.`
        );
      }
    }
    return this.focusState[key];
  }

  useGenericHooks<K extends keyof T>(
    keys: string[],
    stateValuesKey: string
  ): Record<string, any> {
    const keysVal: Record<string, any> = {};
    keys.forEach(key => {
      if (key !== stateValuesKey) {
        const [value] = useAtom(this.getFocusItem(key));
        keysVal[key] = value;
      }
    });

    keysVal[stateValuesKey] = { ...keysVal };
    return keysVal;
  }

  useState<K extends keyof T>(
    keys: string[],
    stateValuesKey: string = 'stateValues'
  ): Record<string, any> {
    return this.useGenericHooks(keys, stateValuesKey);
  }

  useCompleteState(): T {
    const completeState = useAtom(this.state);
    return completeState[0];
  }

  useScopeState<K extends keyof T>(key: string): [T[K], (value: T[K]) => void] {
    return useAtom(this.getFocusItem(key));
  }

  setState(newState: Partial<T>): void {
    Object.keys(newState).forEach(key => {
      this.getFocusItem(key);
    });

    const updatedState = { ...this.initialState, ...newState };
    this.store.set(this.state, updatedState);
  }

  updateState(newState: Partial<T>): void {
    Object.keys(newState).forEach(key => {
      this.getFocusItem(key);
    });

    const prevState = this.store.get(this.state);
    const updatedState = { ...prevState, ...newState };
    this.store.set(this.state, updatedState);
  }

  getValues(keys: string[]): Record<string, any> {
    const returnValues: Record<string, any> = {};
    keys.forEach(key => {
      returnValues[key] = this.getValue(key);
    });
    return returnValues;
  }

  getValue<K extends keyof T>(key: string): T[K] | null {
    try {
      return this.focusState[key] ? this.store.get(this.focusState[key]) : null;
    } catch {
      throw Error(
        `Key: ${key} does not exist in initial State of ${this.controllerName}`
      );
    }
  }

  getAllValues(): T {
    return this.store.get(this.state);
  }

  reset(): void {
    this.setState(this.initialState);
  }

  resetState(key: string): void {
    this.store.set(this.focusState[key], this.initialState[key as keyof T]);
  }

  memoizedStateUpdate<K extends keyof T>(key: string, value: T[K]): void {
    const oldValue = this.store.get(this.focusState[key]);

    if (isEqual(oldValue, value)) {
      return;
    }

    const prevState = this.store.get(this.state);
    const updatedState = { ...prevState, [key]: value };
    this.store.set(this.state, updatedState);
  }
}
